import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Typewriter } from '@/components/ui/typewriter-text';
import { ScheduleButton } from '@/components/ScheduleButton';
import { VideoModal } from '@/components/VideoModal';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLocalizedRoute } from '@/lib/routes';

export const HeroSection = () => {
  const { t, language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section 
      id="hero"
      className={`relative overflow-hidden flex items-center ${
        isMobile 
          ? 'min-h-[70vh] py-6' 
          : 'min-h-screen lg:min-h-[75vh] xl:min-h-screen'
      }`}
    >
      {/* Hero background image with priority loading */}
      <img 
        src="/lovable-uploads/8acf2e74-bd0e-44b0-8e10-8211f3f5ab19.png"
        alt="PriceZing pricing strategy background"
        width="1920"
        height="1080"
        fetchPriority="high"
        decoding="async"
        className={`absolute inset-0 w-full h-full object-cover object-center-right ${
          isMobile ? '' : 'fixed'
        }`}
        style={{
          minHeight: isMobile ? '70vh' : '75vh',
        }}
      />
      
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-950/70 via-blue-900/75 to-blue-950/85 lg:from-blue-950/55 lg:via-blue-900/60 lg:to-blue-950/65"></div>
      
      {/* Subtle brand accent overlay */}
      <div className="absolute inset-0 bg-gradient-radial from-orange-600/15 via-transparent to-transparent opacity-30"></div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl" style={{ minHeight: isMobile ? '500px' : '450px' }}>
          <div className="animate-slide-up space-y-6">
            <Badge 
              variant="secondary" 
              className="mb-6 shadow-lg backdrop-blur-sm bg-white/10 text-white border-white/20"
            >
              {language === 'en' ? 'Trusted by 300+ Companies' : 'Confiada por 300+ empresas'}
            </Badge>
            
            <h1 
              className="font-bold text-white leading-none tracking-tight mb-6"
              style={{
                fontSize: isMobile ? 'clamp(2rem, 8vw, 3.5rem)' : 'clamp(2.5rem, 6vw, 4.5rem)',
                lineHeight: '1.05',
                letterSpacing: '-0.02em',
                maxWidth: '720px',
                minHeight: isMobile ? '180px' : '200px',
                display: 'flex',
                alignItems: 'flex-start'
              }}
            >
              <Typewriter
                text={language === 'en' 
                  ? ["Real-Time Price Monitoring, Smarter Decisions", "Value-Based Pricing Strategies That Boost Profits", "Enter New Markets with Defendable Pricing"]
                  : ["Monitoreo de Precios en Tiempo Real, Decisiones Más Inteligentes", "Estrategias de Precios Basadas en Valor que Aumentan Ganancias", "Ingresa a Nuevos Mercados con Precios Defendibles"]
                }
                speed={150}
                loop={true}
                deleteSpeed={60}
                delay={3500}
                className="font-bold text-white leading-none tracking-tight"
              />
            </h1>
            
            <p 
              className="text-white/90 mb-8 max-w-2xl"
              style={{
                fontSize: isMobile ? 'clamp(0.9rem, 4vw, 1.1rem)' : 'clamp(1rem, 2.2vw, 1.25rem)',
                maxWidth: '65ch'
              }}
            >
              {t.heroSubtitle}
            </p>
            
            <div className={`flex gap-4 pt-4 ${isMobile ? 'flex-col space-y-3' : 'flex-col sm:flex-row'}`}>
              <ScheduleButton
                locale={language}
                variant="default"
                size={isMobile ? "default" : "lg"}
                source="hero"
                intent="diagnostic"
                className="hero-cta-primary shadow-strong transition-all hover:scale-105 hover:shadow-xl"
              />
              <Link to={getLocalizedRoute('about', language)}>
                <Button 
                  variant="outline" 
                  size={isMobile ? "default" : "lg"}
                  className="border-white/50 text-white hover:bg-white hover:text-blue-900 transition-all hover:scale-105 backdrop-blur-sm bg-white/5 w-full sm:w-auto"
                >
                  {t.learnMoreAbout}
                </Button>
              </Link>
              <VideoModal
                videoId="grHReagegY4"
                buttonText="VIDEO"
                buttonVariant="outline"
                buttonSize={isMobile ? "default" : "lg"}
                buttonClassName="border-white/50 text-white hover:bg-white hover:text-blue-900 transition-all hover:scale-105 backdrop-blur-sm bg-white/5 w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Responsive styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (prefers-reduced-motion: reduce) {
            .animate-slide-up {
              animation: none;
            }
          }
          
          .bg-gradient-radial {
            background: radial-gradient(circle at bottom left, var(--tw-gradient-stops));
          }
          
          @media (max-width: 768px) {
            #hero {
              background-attachment: scroll !important;
            }
          }
        `
      }} />
    </section>
  );
};